import { useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { motion } from 'motion/react';
import { Download } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { annualReportUrl, annualReports } from '@/lib/annual-reports';
import { useTranslation } from '@/lib/i18n';
import { DiamondGrid, diamondGlows } from '../primitives';
import { DRAG_TOUCH_ACTION, useCarouselDrag } from './use-carousel-drag';

/**
 * The reports are the ones the transparency page already links to — `src/lib/annual-reports.ts` is their
 * single home, so a new memoria is added once and both pages pick it up.
 *
 * REVERSED here because that list is oldest-first and this rail runs newest at the top; the copy keeps
 * the shared data from having either page's ordering baked into it.
 */
const REPORTS = [...annualReports].reverse();

/** The hand-off opens on 2023, which is two down from the newest. */
const OPENS_ON = 2;

/** The rail marker, an 8px brand disc with a 4px hole — the same figure the milestone rail uses. */
function YearMarker() {
	return (
		<span aria-hidden className="bg-brand relative block size-2 rounded-full">
			<span className="absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
		</span>
	);
}

/**
 * "Nuestro impacto, año a año" — the annual reports carousel.
 *
 * The copy sits left with a footnote beneath it, one sand card holds the selected year, and the rail on
 * the right is VERTICAL here (newest at the top) rather than horizontal like the milestones one.
 *
 * The card frame clips its track, so at rest exactly one year shows — as drawn — while a drag still
 * slides the next one in. Dragging is horizontal even though the rail runs vertically: a vertical drag
 * would have to claim the page's own scroll gesture on a phone.
 */
export function Reports() {
	const { t } = useLingui();
	const { language } = useTranslation();
	const [active, setActive] = useState(OPENS_ON);
	const frame = useRef<HTMLDivElement>(null);
	const last = REPORTS.length - 1;

	const { drag, dragged, panHandlers } = useCarouselDrag({
		active,
		last,
		onChange: setActive,
		// One card fills the frame, so the frame's own width IS the step.
		step: () => frame.current?.clientWidth ?? 1,
	});

	return (
		<section className="relative overflow-hidden bg-white">
			{/* A single brand glow running off the bottom-left corner: the export puts a 777x501 ellipse at
			    left -398 with its top 352.5px below the centre line, which centres it at (-9.5, 726.5). */}
			<DiamondGrid
				color="var(--color-brand)"
				opacity={0.95}
				masks={[diamondGlows({ x: '-10px', y: 'calc(100% - 22px)' })]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col gap-10 px-6 py-16 md:py-20 xl:h-[748px] xl:gap-0 xl:px-0 xl:py-0">
				<div className="flex w-full max-w-[513px] flex-col gap-4 xl:absolute xl:top-[183px] xl:left-[93px]">
					<h2 className="text-ink text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`Our impact, year by year`}
					</h2>
					<p className="text-ink max-w-[496px] text-lg leading-[1.5] tracking-[-0.02em] md:text-xl">
						{t`We invite you to consult our annual corporate reports for the detail of the social impact achieved.`}
					</p>
				</div>

				{/* The card and the rail travel together on mobile, so they stay one control. */}
				<div className="flex items-center gap-4 md:gap-8 xl:contents">
					{/* 529x383 at left 707. `overflow-hidden` is doing real work: it is what keeps a single year
					    on screen at rest while the track behind it holds all seven. */}
					<motion.div
						ref={frame}
						{...panHandlers}
						style={{ touchAction: DRAG_TOUCH_ACTION }}
						className="bg-sand relative aspect-[529/383] min-w-0 flex-1 cursor-grab overflow-hidden rounded-3xl select-none active:cursor-grabbing xl:absolute xl:top-[183px] xl:left-[707px] xl:aspect-auto xl:h-[383px] xl:w-[529px] xl:flex-none"
					>
						<motion.div style={{ x: drag }} className="flex h-full">
							{REPORTS.map((report, i) => (
								<div
									key={report.year}
									// `overflow-hidden` on the SLIDE, not just the frame: the illustration overhangs the
									// card's right edge by design, and without this it spills into the neighbouring slide
									// instead of being cropped — which showed up as the previous year's drawing leaking
									// into the left of the visible card.
									className="relative h-full w-full shrink-0 overflow-hidden transition-transform duration-500 ease-out"
									style={{ transform: `translateX(${-active * 100}%)` }}
									aria-hidden={i !== active}
								>
									{/* 416x373 at (186, 36) of the card, which overflows its right edge on purpose.
									    PROVISIONAL: the hand-off has ONE drawing, 2023's, and it stands in for all seven years
									    until the rest arrive, so the rail repeats it. `object-contain` keeps it undistorted
									    inside a slot whose aspect is not its own.
									
									    Decorative, hence no alt and no msgid: the year and the download link beside it already
									    name the card, and a msgid for scaffolding that gets replaced the moment the other six
									    drawings land would cost ten translations for nothing. */}
									<img
										src="/redesign/about/high-five.svg"
										alt=""
										aria-hidden
										className="pointer-events-none absolute top-[9.4%] left-[35.2%] h-[97.4%] w-[78.6%] object-contain"
									/>

									{/* 72px, right-aligned in a 175 box at (44, 33) of the card. #F86A00 is the export's
									    own value here and is a shade off `brand-strong` (#F66900), so it stays literal. */}
									<p className="absolute top-[8.6%] left-[8.3%] w-[33.1%] text-right text-5xl leading-[1.3] font-medium text-[#F86A00] tabular-nums md:text-[4.5rem]">
										{report.year}
									</p>

									{/* (48, 316) of the card. The click guard matters here in a way it does not on the
									    milestone cards: without it, releasing a drag over the link would open a PDF. */}
									<a
										href={annualReportUrl(report.links, language)}
										target="_blank"
										rel="noopener noreferrer"
										onClick={(e) => dragged.current && e.preventDefault()}
										tabIndex={i === active ? undefined : -1}
										className="text-ink absolute top-[82.5%] left-[9.1%] flex items-center gap-2 text-[0.9375rem] leading-[1.3] font-medium tracking-[-0.02em] transition-opacity hover:opacity-70"
									>
										{t`Download`}
										<Download className="size-4" />
									</a>
								</div>
							))}
						</motion.div>
					</motion.div>

					{/* The rail: a two-column grid so the marker lands in the active year's own ROW without any
					    measuring, and the line is one more item spanning every row of the first column. */}
					<div
						className="grid shrink-0 gap-x-[6px] gap-y-2 xl:absolute xl:top-[266px] xl:left-[1302px]"
						style={{ gridTemplateColumns: 'auto auto', gridTemplateRows: `repeat(${REPORTS.length}, auto)` }}
					>
						<span
							aria-hidden
							style={{ gridColumn: 1, gridRow: '1 / -1' }}
							// Inset 8px top and bottom, as the export's 201px line is inside a 216px column. Its
							// colour is not in the export; 20% matches the inactive years it sits beside.
							className="bg-ink/20 my-2 w-px justify-self-center"
						/>
						<span
							aria-hidden
							style={{ gridColumn: 1, gridRow: active + 1 }}
							className="self-center justify-self-center"
						>
							<YearMarker />
						</span>
						{REPORTS.map((report, i) => (
							<button
								key={report.year}
								type="button"
								onClick={() => setActive(i)}
								aria-current={i === active ? 'true' : undefined}
								style={{ gridColumn: 2, gridRow: i + 1 }}
								className={cn(
									'text-ink cursor-pointer text-base leading-[1.5] font-medium tracking-[-0.02em] transition-opacity',
									// 20% when inactive, not the 50% the milestone rail uses — this rail is quieter.
									i === active ? 'opacity-100' : 'opacity-20 hover:opacity-50',
								)}
							>
								{report.year}
							</button>
						))}
					</div>
				</div>

				{/* 533 wide at (93, 544), 14px at 70%. */}
				<p className="text-ink/70 w-full max-w-[533px] text-sm leading-[1.5] tracking-[-0.02em] xl:absolute xl:top-[544px] xl:left-[93px]">
					{t`*Financial reports audited annually by AUDRIA Auditoría y Consultoría, S.L.P.`}
				</p>
			</div>
		</section>
	);
}
